import { WorkspaceNavigationService } from '../../../services/workspace-navigation.service';
import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { LeadApiService, LeadProfile, SaveLeadProfile } from '../../../services/lead-api.service';
import {
  BusinessTypeLookup,
  LeadAssigneeLookup,
  LeadStageLookup,
  LookupApiService,
} from '../../../services/lookup-api.service';
import { AssignableRep } from './leads-board.component';
import { AppContextService } from '../../../services/app-context.service';
import { parseLocalDateString, toLocalDateString } from '../../../shared/date.util';
import { MANAGE_ANY, MANAGE_OWN } from '../sales-route/sales-route.models';

import {
  LeadRecordSection,
  NextAction,
  leadRecord,
  nextActionHeadline,
  nextActionOf,
} from './lead-record';

export type LeadPageMode = 'new' | 'edit' | 'view';

interface LeadPageAgentContext {
  readonly mode: LeadPageMode;
  readonly leadId: number | null;
}

/** One routed page template for the complete New / Edit / View lifecycle. */
@Component({
  selector: 'app-lead-page',
  templateUrl: './lead-page.component.html',
  styleUrls: ['./lead-page.component.css'],
  host: { class: 'workspace-page', '[class.workspace-page--full]': '!inline' },
})
export class LeadPageComponent implements OnInit, OnDestroy {
  @Input() inline = false;
  @Input() showEditAction = true;
  @Input() leadId: number | null = null;
  // Cached reads must not update the containing summary during its own check.
  @Output() profileLoaded = new EventEmitter<LeadProfile>(true);
  mode: LeadPageMode;
  readonly collectionView: 'table' | 'board' | null;
  form: FormGroup;
  profile: LeadProfile | null = null;
  loading = false;
  saving = false;
  stagesLoading = true;
  repsLoading = true;
  businessTypesLoading = true;
  error = '';
  stages: LeadStageLookup[] = [];
  /** Every category a lead may be, for the picker to offer. */
  businessTypes: BusinessTypeLookup[] = [];
  /** The loaded record, field by field. Empty until the profile arrives. */
  record: LeadRecordSection[] = [];
  nextAction: NextAction = { state: 'none', type: null, due: null, days: null };
  reps: LeadAssigneeLookup[] = [];
  private readonly destroyed$ = new Subject<void>();
  private readonly agentContextState = new BehaviorSubject<LeadPageAgentContext>({
    mode: 'view',
    leadId: null,
  });
  private agentContextRegistration?: Subscription;


  /**
   * The roster, keyed the way the record names owners. The lookup calls the
   * id `id` and the board calls it `userId`, so this one does have to map.
   *
   * A field rebuilt when the roster lands - NOT a getter. As a getter this
   * mapped a fresh array on every change-detection pass, and it is bound as
   * the record's `[reps]` input: a new reference each pass meant ngOnChanges
   * on the record each pass, which reloaded the lead, whose response ran
   * change detection again. The View Lead page sat in that loop firing
   * GET /api/lead-work-items/{id} until the browser refused to send more
   * (net::ERR_INSUFFICIENT_RESOURCES) and rendered nothing.
   */
  boardReps: AssignableRep[] = [];

  constructor(
    fb: FormBuilder,
    private readonly api: LeadApiService,
    private readonly lookups: LookupApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    public readonly auth: AuthService,
    private readonly agentContext: AppContextService,
    private readonly navigation?: WorkspaceNavigationService,
  ) {
    this.mode = route.snapshot.data['leadPageMode'] as LeadPageMode ?? 'view';
    const parentPath = route.parent?.routeConfig?.path;
    this.collectionView = parentPath === 'table' || parentPath === 'board'
      ? parentPath
      : null;
    this.form = fb.group({
      name: ['', [Validators.required, Validators.maxLength(256)]],
      leadStageId: [null as number | null, Validators.required],
      salesRepUserId: [null as number | null],
      // Ids, not prose: the categories are a closed list the server owns, and
      // a lead may be several of them.
      businessTypeIds: [[] as number[]],
      origin: ['manual', Validators.required],
      addressLine1: ['', Validators.maxLength(256)],
      city: ['', Validators.maxLength(128)],
      state: ['', Validators.maxLength(64)],
      postalCode: ['', Validators.maxLength(32)],
      latitude: [null as number | null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null as number | null, [Validators.min(-180), Validators.max(180)]],
      probability: [null as number | null, [Validators.min(0), Validators.max(1)]],
      expectedAnnualRevenue: [null as number | null],
      expectedAnnualContribution: [null as number | null],
      nextActionType: ['', Validators.maxLength(64)],
      nextActionDue: [null as string | null],
    });
    if (this.isView) this.form.disable({ emitEvent: false });
  }

  get isNew(): boolean { return this.mode === 'new'; }
  get isEdit(): boolean { return this.mode === 'edit'; }
  get isView(): boolean { return this.mode === 'view'; }
  /**
   * The heading names the lead it is about.
   *
   * "View Lead" alone is the same six characters on every lead in the system;
   * with two records open in two tabs it is the browser tab title too, and
   * neither said which one you were looking at. The name follows the verb
   * rather than replacing it, so the mode is still legible.
   *
   * Absent until the profile arrives, and absent for a lead saved without a
   * name - "View Lead: " with nothing after it would read as a loading state
   * that never finished.
   */
  get title(): string {
    if (this.isNew) return 'New Lead';
    const named = this.profile?.name?.trim();
    if (this.isEdit) return named ? `Edit Lead: ${named}` : 'Edit Lead';
    return named ? `View Lead: ${named}` : 'View Lead';
  }
  get viewSubtitle(): string {
    const business = this.record.find(section => section.title === 'Lead')?.rows
      .find(row => row.label === 'Business types' && !row.empty)?.value;
    const address = [this.profile?.addressLine1, this.profile?.city, this.profile?.state]
      .filter(Boolean).join(', ');
    return [business, address].filter(Boolean).join(' · ');
  }

  get submitLabel(): string { return this.isEdit ? 'Update Lead' : 'Create Lead'; }

  /** The one sentence the view leads with. */
  get nextActionHeadline(): string { return nextActionHeadline(this.nextAction); }

  get nextActionIcon(): string {
    switch (this.nextAction.state) {
      case 'overdue': return 'error_outline';
      case 'today': return 'today';
      case 'upcoming': return 'event';
      default: return 'event_busy';
    }
  }

  ngOnInit(): void {
    if (this.inline) {
      this.mode = 'view';
      this.form.disable({ emitEvent: false });
    }
    this.publishAgentContext();
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = this.agentContext.register({
      key: 'lead-detail',
      name: 'Current Lead page',
      state$: this.agentContextState,
      describe: state => this.describeAgentContext(state),
    });
    this.loadStages();
    this.loadAssignees();
    this.loadBusinessTypes();
    if (this.inline) {
      this.loadProfile();
    } else if (!this.isNew) {
      this.route.paramMap.pipe(
        map(params => {
          const id = Number(params.get('id'));
          return Number.isInteger(id) && id > 0 ? id : null;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      ).subscribe(id => {
        this.leadId = id;
        this.publishAgentContext();
        this.loadProfile();
      });
    }
  }

  ngOnDestroy(): void {
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = undefined;
    this.agentContextState.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private publishAgentContext(): void {
    this.agentContextState.next({ mode: this.mode, leadId: this.leadId });
  }

  private describeAgentContext(state: LeadPageAgentContext): string {
    if (state.leadId !== null) {
      const mode = state.mode === 'edit' ? 'Edit' : 'View';
      return `Current Lead ID: ${state.leadId}. Page mode: ${mode}.`;
    }
    return state.mode === 'new'
      ? 'The New Lead form is open; it does not have a Lead ID yet.'
      : 'No valid Lead ID is open.';
  }

  save(): void {
    if (this.isView || this.loading || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    if ((raw.latitude == null) !== (raw.longitude == null)) {
      this.error = 'Latitude and longitude must be entered together.';
      return;
    }
    this.saving = true;
    this.error = '';
    const profile: SaveLeadProfile = {
      name: raw.name.trim(),
      leadStageId: raw.leadStageId,
      salesRepUserId: raw.salesRepUserId,
      businessTypeIds: raw.businessTypeIds ?? [],
      origin: raw.origin,
      addressLine1: this.text(raw.addressLine1),
      city: this.text(raw.city),
      state: this.text(raw.state),
      postalCode: this.text(raw.postalCode),
      latitude: raw.latitude,
      longitude: raw.longitude,
      probability: raw.probability,
      expectedAnnualRevenue: raw.expectedAnnualRevenue,
      expectedAnnualContribution: raw.expectedAnnualContribution,
      nextActionType: this.text(raw.nextActionType),
      nextActionDue: raw.nextActionDue,
    };
    const request = this.isEdit
      ? this.api.update(this.leadId!, profile)
      : this.api.create(profile);
    request.pipe(takeUntil(this.destroyed$)).subscribe({
      next: result => {
        this.saving = false;
        this.snackBar.open(
          this.isEdit ? 'Lead updated' : 'Lead created',
          undefined,
          { duration: 2500 },
        );
        if (this.inline) {
          this.mode = 'view';
          this.form.disable({ emitEvent: false });
          this.publishAgentContext();
          this.loadProfile();
          return;
        }
        this.router.navigate(this.collectionView
          ? ['/leads', this.collectionView, result.id]
          : ['/leads', result.id], { queryParams: this.tabParam, state: this.navigation?.returnState });
      },
      error: response => {
        this.saving = false;
        this.error = response.error?.error ||
          (this.isEdit ? 'Could not update lead.' : 'Could not create lead.');
      },
    });
  }

  /**
   * Back to where the editing started, tab and all.
   *
   * Cancel used to return to the record's first tab whatever had been open,
   * so somebody reading the quotes, pressing Edit and changing their mind was
   * put back at the top of the lead. The tab travels on the URL, so returning
   * to it is just carrying the parameter back.
   */
  cancel(): void {
    if (this.inline) {
      this.mode = 'view';
      this.form.disable({ emitEvent: false });
      this.publishAgentContext();
      this.loadProfile();
      return;
    }
    if (!this.isEdit) {
      this.router.navigate(['/leads', this.collectionView ?? 'table']);
      return;
    }
    this.router.navigate(
      this.collectionView
        ? ['/leads', this.collectionView, this.leadId]
        : ['/leads', this.leadId],
      { queryParams: this.tabParam, state: this.navigation?.returnState });
  }

  /** The tab that was open when the edit began, ready to be handed back. */
  private get tabParam(): Record<string, string> {
    const params: Record<string, string> = {};
    for (const key of ['tab', 'routeDate', 'routeOwner', 'routeId']) {
      const value = this.route.snapshot.queryParamMap.get(key);
      if (value) params[key] = value;
    }
    return params;
  }

  get backLabel(): string { if (this.routeReturnDate) return 'Back to route'; return this.navigation?.returnLabel ? `Back to ${this.navigation.returnLabel}` : 'Back to Leads'; }

  back(): void {
    if (this.routeReturnDate) {
      void this.router.navigate(['/leads/sales-route'], { queryParams: {
        date: this.routeReturnDate,
        routeId: this.route.snapshot.queryParamMap.get('routeId'),
        owner: this.route.snapshot.queryParamMap.get('routeOwner'),
      } });
      return;
    }
    if (this.navigation?.returnToSource()) return;
    this.router.navigate(['/leads', this.collectionView ?? 'table']);
  }

  get routeReturnDate(): string | null {
    if (this.inline) return null;
    const raw = this.route.snapshot.queryParamMap?.get('routeDate');
    const parsed = raw ? parseLocalDateString(raw) : null;
    return parsed && toLocalDateString(parsed) === raw ? raw : null;
  }

  get canPlanVisit(): boolean {
    return this.auth.hasPermission(MANAGE_ANY) || this.auth.hasPermission(MANAGE_OWN);
  }

  planVisit(): void {
    if (!this.profile || !this.canPlanVisit) return;
    void this.router.navigate(['/leads/sales-route'], { queryParams: {
      lead: this.profile.id,
      date: this.routeReturnDate,
      owner: this.auth.hasPermission(MANAGE_ANY)
        ? (this.routeReturnDate ? this.route.snapshot.queryParamMap.get('routeOwner') : this.profile.salesRepUserId)
        : null,
    } });
  }

  edit(): void {
    if (this.inline) {
      if (!this.auth.hasPermission('Lead.Manage')) return;
      this.mode = 'edit';
      this.form.enable({ emitEvent: false });
      this.publishAgentContext();
      return;
    }
    this.router.navigate(
      this.collectionView
        ? ['/leads', this.collectionView, this.leadId, 'edit']
        : ['/leads', this.leadId, 'edit'],
      { queryParams: this.tabParam, state: this.navigation?.returnState });   // so Cancel can put it back
  }

  /** The record is built from the profile AND the assignee roster, which
   *  arrive from two requests in either order — so it is rebuilt whenever
   *  one of them lands, or the owner reads as "User 12" for as long as the
   *  roster is in flight. */
  private rebuildRecord(): void {
    if (!this.profile) { this.record = []; return; }
    this.record = leadRecord(this.profile, this.reps);
    this.nextAction = nextActionOf(this.profile);
  }

  /** After the record writes - a stage move, an owner change - so the fields
   *  the other tabs print do not keep saying what was true before it. */
  reloadProfile(): void {
    if (this.isView) this.loadProfile();
  }

  private loadProfile(): void {
    if (this.leadId === null) {
      this.error = 'A lead id is required.';
      return;
    }
    const requestedId = this.leadId;
    if (!this.inline || this.profile?.id !== requestedId) this.profile = null;
    this.loading = true;
    this.error = '';
    this.api.getProfile(requestedId).pipe(takeUntil(this.destroyed$)).subscribe({
      next: profile => {
        if (this.leadId !== requestedId) return;
        this.profile = profile;
        this.profileLoaded.emit(profile);
        this.form.patchValue({
          ...profile,
          businessTypeIds: profile.businessTypes.map(type => type.id),
        });
        this.rebuildRecord();
        this.loading = false;
      },
      error: () => {
        if (this.leadId !== requestedId) return;
        this.error = 'Could not load this lead.';
        this.loading = false;
      },
    });
  }

  private loadStages(): void {
    this.lookups.leadStages().pipe(takeUntil(this.destroyed$)).subscribe({
      next: stages => {
        this.stages = stages
          .sort((a, b) => a.sortOrder - b.sortOrder);
        this.stagesLoading = false;
        const firstActive = this.stages.find(stage => stage.isActive);
        if (this.isNew && this.form.get('leadStageId')!.value == null && firstActive)
          this.form.get('leadStageId')!.setValue(firstActive.id);
      },
      error: () => {
        this.stagesLoading = false;
        this.error ||= 'Could not load lead stages.';
      },
    });
  }

  private loadBusinessTypes(): void {
    this.lookups.businessTypes().pipe(takeUntil(this.destroyed$)).subscribe({
      next: types => {
        this.businessTypes = types;
        this.businessTypesLoading = false;
      },
      error: () => {
        this.businessTypesLoading = false;
        this.error ||= 'Could not load business types.';
      },
    });
  }

  private loadAssignees(): void {
    this.lookups.leadAssignees().pipe(takeUntil(this.destroyed$)).subscribe({
      next: reps => {
        this.reps = reps;
        this.boardReps = reps.map(rep => ({
          userId: rep.id, userName: rep.userName, displayName: rep.displayName,
        }));
        this.repsLoading = false;
        this.rebuildRecord();
      },
      error: () => {
        this.repsLoading = false;
        this.error ||= 'Could not load lead owners.';
      },
    });
  }

  private text(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
